import React, {useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {accountTypeNames, MAX_TRANS_LIST_ITEM_COUNT} from "@/constatants";
import {useAccountStore} from "@/store/useAccountStore";
import {SearchParamsUtil} from "@/utils/SearchParamsUtil";
import {TransDto} from "@/common/dto/TransDto";
import {TransFilter} from "@/common/req/TransFilter";
import {FtGrid, FtGridCol} from "@/component/layout/FtGrid";
import {FtButton} from "@/common/FtButton";
import {DateInput} from "@/component/input/DateInput";
import {FtSelect} from "@/component/input/FtSelect";
import {NumberInput} from "@/component/input/NumberInput";
import {StringInput} from "@/component/input/StringInput";
import {TransactionListView, TransactionListViewItem} from "@/component/view/TransactionListView";
import {useDialog} from "@/dialog/DialogContext";
import {LoadableView} from "@/component/view/LoadableView";
import {useTransactions} from "@/hook/useTransactions";

export function SearchTransactionPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const dialog = useDialog();
    const accounts = useAccountStore(state => state.accounts);
    const getAccountNameById = useAccountStore(state => state.getAccountNameById);

    const [transactDateFrom, setTransactDateFrom] = useState(searchParams.get("transactDateFrom") ?? "");
    const [transactDateTo, setTransactDateTo] = useState(searchParams.get("transactDateTo") ?? "");
    const [createTimeFrom, setCreateTimeFrom] = useState(searchParams.get("createTimeFrom") ?? "");
    const [createTimeTo, setCreateTimeTo] = useState(searchParams.get("createTimeTo") ?? "");
    const [accountId, setAccountId] = useState<number | undefined>(SearchParamsUtil.getNumber(searchParams, "accountId"));
    const [amountFrom, setAmountFrom] = useState<number | undefined>(SearchParamsUtil.getNumber(searchParams, "amountFrom"));
    const [amountTo, setAmountTo] = useState<number | undefined>(SearchParamsUtil.getNumber(searchParams, "amountTo"));
    const [searchText, setSearchText] = useState(searchParams.get("search") ?? "");

    const filter = useMemo(() => {
        const filter: TransFilter = {};
        filter.accountId = SearchParamsUtil.getNumber(searchParams, "accountId");
        filter.transactDateFrom = SearchParamsUtil.getYmd(searchParams, "transactDateFrom");
        filter.transactDateTo = SearchParamsUtil.getYmd(searchParams, "transactDateTo");
        filter.createTimeFrom = SearchParamsUtil.getYmd(searchParams, "createTimeFrom");
        filter.createTimeTo = SearchParamsUtil.getYmd(searchParams, "createTimeTo");
        filter.amountFrom = SearchParamsUtil.getNumber(searchParams, "amountFrom");
        filter.amountTo = SearchParamsUtil.getNumber(searchParams, "amountTo");
        filter.searchText = SearchParamsUtil.getString(searchParams, "search");
        return filter;
    }, [searchParams]);

    const searchAvailable = !!(transactDateFrom || transactDateTo || createTimeFrom || createTimeTo || accountId || amountFrom || amountTo || searchText);
    const searchEnabled = JSON.stringify(filter) !== '{}';

    const {data: transactions, error} = useTransactions(filter, searchEnabled);

    const items: TransactionListViewItem[] | undefined = useMemo(() => {
        if (!transactions) return undefined;
        return transactions.map(t => ({
            key: t.id.toString(),
            date: t.transactDate,
            type: formatAccount(t),
            inAmount: t.amount,
            outAmount: 0,
            detail: t.detail,
            onClick: () => dialog.openDialog('editTrans', {transId: t.id}),
        }));
    }, [transactions]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (searchAvailable) {
            setSearchParams({
                transactDateFrom,
                transactDateTo,
                createTimeFrom,
                createTimeTo,
                accountId: accountId?.toString() ?? "",
                amountFrom: amountFrom?.toString() ?? "",
                amountTo: amountTo?.toString() ?? "",
                search: searchText,
            });
        }
    };

    function formatAccount(transaction: TransDto) {
        return `${getAccountNameById(transaction.outAccountId)} → ${getAccountNameById(transaction.inAccountId)}`;
    }

    return (
        <div>
            <form method="GET" onSubmit={handleSubmit} style={{marginBottom: '1rem'}}>
                <FtGrid>
                    <FtGridCol>
                        <div style={{display: "flex", alignItems: 'center', gap: 8}}>
                            <div style={{fontSize: 14, width: 40}}>거래일</div>
                            <DateInput value={transactDateFrom} required={false} onChange={(value) => setTransactDateFrom(value ?? "")}/>
                            -
                            <DateInput value={transactDateTo} required={false} onChange={(value) => setTransactDateTo(value ?? "")}/>
                        </div>
                    </FtGridCol>

                    <FtGridCol>
                        <div style={{display: "flex", alignItems: 'center', gap: 8}}>
                            <div style={{fontSize: 14, width: 40}}>입력일</div>
                            <DateInput value={createTimeFrom} required={false} onChange={(value) => setCreateTimeFrom(value ?? "")}/>
                            -
                            <DateInput value={createTimeTo} required={false} onChange={(value) => setCreateTimeTo(value ?? "")}/>
                        </div>
                    </FtGridCol>

                    <FtGridCol>
                        <div style={{display: 'flex'}}>
                            <div style={{display: "flex", alignItems: 'center', gap: 8}}>
                                <div style={{fontSize: 14, width: 40}}>금액</div>
                                <NumberInput value={amountFrom} onChange={v => setAmountFrom(v)}/>
                                -
                                <NumberInput value={amountTo} onChange={v => setAmountTo(v)}/>
                            </div>
                        </div>
                    </FtGridCol>

                    <FtGridCol>
                        <div style={{display: "flex", gap: 8}}>
                            <FtSelect
                                emptyLabel="모든 계정"
                                width={100}
                                values={accounts.map(a => a.id.toString())}
                                labels={accounts.map(a => `${accountTypeNames[a.type]} / ${a.name}`)}
                                value={accountId?.toString() ?? ""}
                                onChange={value => setAccountId(value ? parseInt(value) : undefined)}
                            />

                            <StringInput placeholder="검색어" value={searchText} onChange={setSearchText}/>

                            <div style={{flex: 1}}>
                                <FtButton type="submit" disabled={!searchAvailable} style={{display: 'block', width: '100%'}}>검색</FtButton>
                            </div>
                        </div>
                    </FtGridCol>
                </FtGrid>
            </form>

            {searchEnabled && <LoadableView data={items} error={error} component={(items) => <TransactionListView splitInOut={false} items={items}/>}/>}

            {(items?.length ?? 0) === MAX_TRANS_LIST_ITEM_COUNT && (
                <div style={{marginTop: '1rem', color: '#999', fontSize: 12, textAlign: 'center'}}>
                    검색 결과가 {MAX_TRANS_LIST_ITEM_COUNT}건으로 제한됩니다. 조건을 추가하여 검색해 주세요.
                </div>
            )}
        </div>
    );
}
